import React, { useState, useEffect } from 'react';
import { useUser } from 'util/UserContext';
import Modal from 'ui/Modal';
import { Drawer, DrawerContent } from 'ui/Drawer';
import api from 'config/axiosConfig';
import FollowButton from 'components/Common/FollowButton';
import EntityAvatar from 'components/Entities/EntityAvatar';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import { getActorDetails } from 'util/getObjectDetails';
import { Action } from 'util/constants';
import DialogConfirmation from 'ui/DialogConfirmation';
import { apiRoutes } from "util/apiRoutes";
import axios from "axios";
import type { EntityData } from "util/getObjectDetails";
import EmptyState from 'ui/EmptyState';

interface Participant {
  participant_id: number;
  action_id: number;
  properties: {
    status: "active" | "pending";
    created_at: string;
  };
  username?: string;
  display_name?: string;
  properties_public?: Record<string, unknown>;
}

type EnrichedParticipant = Participant & {
  id: number;
  type: "user";
  participant_entity: EntityData;
};

interface Props {
  actionId: number;
  onClose: () => void;
}

const ParticipantsModal: React.FC<Props> = ({ actionId, onClose }) => {
  const { userIsMobile, currentUser } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<any[]>([]);
  const [action, setAction] = useState<Action | null>(null);
  const [isCoauthorOrCreator, setIsCoauthorOrCreator] = useState(false);
  const [participantToRemove, setParticipantToRemove] = useState<any | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    const fetchActionDetails = async () => {
      try {
        const response = await api.get<Action>(apiRoutes.posts.byId(actionId));
        const fetchedAction = response.data;
        setAction(fetchedAction);

        if (currentUser) {
          const isCreator = fetchedAction.user_id === currentUser.id;
          const { isCoauthor } = await api.get(apiRoutes.coauthors.status("action", actionId)).then(res => res.data);
          setIsCoauthorOrCreator(isCreator || isCoauthor);
        }
      } catch (error) {
        console.error('Error fetching action details:', error);
      }
    };
    fetchActionDetails();
  }, [actionId, currentUser]);

  useEffect(() => {
    const fetchParticipants = async () => {
      try {
        const response = await api.get(apiRoutes.posts.participants.list(actionId));
        
        // Check if we have permission to view participants
        if (!response.data.canView) {
          setError(response.data.message || 'You do not have permission to view participants');
          setLoading(false);
          return;
        }

        // Enrich the participants data
        const enriched = await Promise.all(response.data.participants.map(async (participant: Participant) => {
          const enrichedData = await getActorDetails('user', participant.participant_id);
          
          return {
            ...participant,
            id: participant.participant_id,
            type: 'user',
            participant_entity: enrichedData
          };
        }));

        setParticipants(enriched);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.data && typeof (error.response.data as { message?: string }).message === "string") {
          setError((error.response.data as { message: string }).message);
        } else {
          setError("Failed to load participants.");
        }
        console.error("Error fetching participants:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchParticipants();
  }, [actionId]);

  const handleRemoveClick = (participant: EnrichedParticipant) => {
    setParticipantToRemove(participant);
    setIsDialogOpen(true);
  };

  const cancelRemove = () => {
    setParticipantToRemove(null);
    setIsDialogOpen(false);
  };

  const confirmRemove = async () => {
    if (!participantToRemove) return;

    try {
      await api.delete(apiRoutes.posts.participants.byUser(actionId, participantToRemove.id));
      setParticipants(prev => prev.filter(p => p.id !== participantToRemove.id));
    } catch (error) {
      console.error('Error removing participant:', error);
      setError('Failed to remove participant.');
    } finally {
      setParticipantToRemove(null);
      setIsDialogOpen(false);
    }
  };

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
    // Keep drawer visible - let dialog overlay on top
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const content = (
    <div className="flex flex-col space-y-4">
      {loading ? (
        <LoadingSkeleton count={10} variant="entity-avatar" avatarSize="h-9 w-9" margin="mx-2"/>
      ) : error ? (
        <div className="text-warning-red dark:text-warning-red-dark text-center p-4">
          {error}
        </div>
      ) : participants.length === 0 ? (
        <EmptyState
          title="No participants yet"
          subtitle="Be the first to join, and invite others!"
          className="p-4 mt-4"
        />
      ) : (
        <div className="space-y-2">
          {participants.map((participant) => (
            <div key={`user-${participant.id}`} className="flex items-center justify-between p-2">
              <EntityAvatar
                entity={participant.participant_entity}
                entityType="user"
                entityChip={true}
                showCloseChip={isCoauthorOrCreator && participant.id !== action?.user_id}
                onEntityChipClose={() => handleRemoveClick(participant)}
                isInputField={false}
                showLink={true}
              />
              {participant.id !== currentUser?.id && (
                <FollowButton
                  entityId={participant.id}
                  entityType="user"
                  entityName={participant.participant_entity?.username}
                  isPrivate={participant.participant_entity?.properties_public?.is_private}
                  onDialogOpen={handleDialogOpen}
                  onDialogClose={handleDialogClose}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      {participantToRemove && (
        <DialogConfirmation
          isOpen={!!participantToRemove}
          onConfirm={confirmRemove}
          onCancel={cancelRemove}
          title="Remove Participant"
          description={`Are you sure you want to remove ${participantToRemove.participant_entity?.display_name || participantToRemove.participant_entity?.username} from this action?`}
          confirmText="Remove"
        />
      )}
      {userIsMobile ? (
        <Drawer 
          open={true} 
          onOpenChange={(open) => {
            // Don't close the drawer if a dialog is open
            if (!open && !isDialogOpen) {
              onClose();
            }
          }}
        >
          <DrawerContent title="Participants">
            {content}
          </DrawerContent>
        </Drawer>
      ) : (
        <Modal 
          isOpen={true} 
          onClose={onClose} 
          title="Participants"
        >
          {content}
        </Modal>
      )}
    </>
  );
};

export default ParticipantsModal; 